// Account menu — an avatar trigger that opens a small popover with the user's
// identity, a link to Settings, and Log out. One instance, in the shell header,
// for every role: it used to have a second full-width form at the foot of the
// sidebar, so where a person signed out from depended on whether their sidebar
// happened to be collapsed. Closes on outside click / Escape. Purely
// presentational + navigation; the authoritative account lives in the app's own
// profile store (see SettingsPage for reads/writes).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleLabel, workspaceLabel } from '@/lib/auth';
import { useClaims } from '@/hooks/useSession';
import { signOut } from '@/lib/session';
import { fontPx } from '@/lib/fontSize';

export function ProfileMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const claims = useClaims();
  // A school-managed student has no real email — their identity is the roll or
  // admission number they sign in with, so fall back to their name.
  const email = claims?.email ?? claims?.name ?? '';
  const initial = (email[0] ?? 'U').toUpperCase();
  const role = roleLabel(claims);
  const workspace = workspaceLabel(claims);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = () => {
    setBusy(true);
    // signOut() revokes the Keycloak session and then hard-navigates to /login,
    // so there is nothing to navigate to here. Doing so as well would race that
    // redirect and could leave the SSO session alive.
    void signOut();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Account"
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
        }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div
          data-numeric=""
          className="grid flex-none place-items-center rounded-full font-bold"
          style={{
            height: 34,
            width: 34,
            background: 'var(--brand-lime)',
            color: 'var(--brand-forest)',
            fontSize: fontPx(14),
          }}
        >
          {initial}
        </div>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            zIndex: 50,
            minWidth: 220,
            top: 'calc(100% + 8px)',
            right: 0,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 10px 30px rgba(0,0,0,.16)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-default)' }}>
            <div
              style={{
                fontSize: fontPx(13),
                fontWeight: 700,
                color: 'var(--text-body)',
                wordBreak: 'break-all',
              }}
            >
              {email || 'Account'}
            </div>
            {/* Role first, workspace second: which authority you are acting with
                matters more than where, and a school admin who has forgotten they
                are signed in as an admin is the mistake worth preventing. */}
            <div style={{ fontSize: fontPx(11.5), color: 'var(--text-hint)', marginTop: 2 }}>{role}</div>
            {workspace && (
              <div style={{ fontSize: fontPx(11.5), color: 'var(--text-hint)', marginTop: 1 }}>
                {workspace}
              </div>
            )}
          </div>
          {claims && claims.memberships.length > 1 && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                navigate('/choose-workspace');
              }}
            >
              Switch workspace
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          >
            Profile & settings
          </MenuItem>
          <MenuItem danger disabled={busy} onClick={handleLogout}>
            {busy ? 'Logging out…' : 'Log out'}
          </MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  danger = false,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  // Hover lives in `.cwy-menu-option` (globals.css) rather than in React state:
  // a tap on a touch device fires mouseenter and never fires mouseleave, so the
  // JS version left the last-tapped row highlighted.
  return (
    <button
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="cwy-menu-option"
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        fontSize: fontPx(13),
        fontWeight: 500,
        border: 'none',
        background: 'transparent',
        color: danger ? 'var(--danger)' : 'var(--text-body)',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
